import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, aiAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CheckCircle2, AlertCircle, ArrowRight, Loader2, Building2, ListTodo,
  FolderKanban, AlertTriangle, ClipboardList, Clock, TrendingUp, TrendingDown,
  GanttChart, CalendarDays, ArrowUpRight, Activity, Sparkles, BarChart3,
  Users, Zap, ListChecks, Briefcase, Presentation, Headphones,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, BarChart, Bar,
} from 'recharts';

const STATUS_COLORS = {
  backlog: '#94a3b8', todo: '#fbbf24', in_progress: '#3b82f6',
  ready_for_testing: '#a78bfa', in_testing: '#a78bfa',
  ready_for_delivery: '#38bdf8', released: '#4ade80',
  done: '#34d399', cancelled: '#f87171',
};
const STATUS_LABELS = {
  backlog: 'Backlog', todo: 'To Do', in_progress: 'In Progress',
  ready_for_testing: 'Ready for Testing', in_testing: 'In Testing',
  ready_for_delivery: 'Ready for Delivery', released: 'Released',
  done: 'Done', cancelled: 'Cancelled',
};

const PERIOD_OPTIONS = [
  { label: '7D', value: 7 },
  { label: '30D', value: 30 },
  { label: '90D', value: 90 },
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activityStats, setActivityStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizationFilter, setOrganizationFilter] = useState('All');
  const [organizations, setOrganizations] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState(30);
  const [aiInsight, setAiInsight] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [lbTab, setLbTab] = useState('tasks');
  const [scope, setScope] = useState('all');
  const [sprintUserStats, setSprintUserStats] = useState(null);
  const [selectedSprintId, setSelectedSprintId] = useState('');

  const isManager = user?.role && ['admin', 'super_admin', 'pm'].includes(user.role);

  useEffect(() => { fetchData(); }, [organizationFilter, scope]);
  useEffect(() => { fetchTrends(); }, [trendPeriod, scope]);
  useEffect(() => { fetchSprintUserStats(); }, [selectedSprintId, scope]);

  const fetchSprintUserStats = async () => {
    try {
      const params = { scope };
      if (selectedSprintId) params.sprint_id = selectedSprintId;
      const res = await dashboardAPI.getSprintUserStats(params);
      setSprintUserStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (organizationFilter !== 'All') params.organization = organizationFilter;
      const [statsRes, lbRes, actRes] = await Promise.all([
        dashboardAPI.getStats(params),
        dashboardAPI.getLeaderboard(scope),
        dashboardAPI.getActivityStats(params),
      ]);
      setStats(statsRes.data);
      setLeaderboard(lbRes.data || []);
      setOrganizations(statsRes.data.organizations || []);
      setActivityStats(actRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchTrends = async () => {
    try {
      const res = await dashboardAPI.getTrends(trendPeriod, scope);
      // API returns { daily: [...], total_created, total_completed }
      // Extract the daily array for the chart
      setTrends(res.data?.daily || res.data || []);
    } catch (e) { console.error(e); }
  };

  const handleAiInsight = async () => {
    setAiLoading(true);
    try { const res = await aiAPI.getDashboardInsights(); setAiInsight(res.data.insights); }
    catch { setAiInsight('Unable to generate.'); }
    finally { setAiLoading(false); }
  };

  if (loading && !stats) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  // Empty state for users with no assigned projects
  if (stats?.no_projects) {
    return (
      <div className="space-y-6 max-w-[1400px]" data-testid="dashboard-page">
        <div>
          <p className="text-sm text-slate-400 mb-1">Welcome,</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{user?.name || 'Dashboard'}</h1>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="no-projects-banner">
          <FolderKanban className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">No Projects Assigned</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            You haven't been assigned to any projects yet. Please contact your Project Manager to get added to a project.
          </p>
        </div>
      </div>
    );
  }

  const t = stats || {};
  const completionRate = t.total > 0 ? Math.round(((t.done || 0) + (t.released || 0)) / t.total * 100) : 0;
  const openCount = (t.total || 0) - (t.done || 0) - (t.released || 0) - (t.cancelled || 0);
  const openRate = t.total > 0 ? Math.round(openCount / t.total * 100) : 0;

  const statusData = [
    { key: 'backlog', value: t.backlog || 0 },
    { key: 'todo', value: t.todo || 0 },
    { key: 'in_progress', value: t.in_progress || 0 },
    { key: 'ready_for_testing', value: t.ready_for_testing || 0 },
    { key: 'in_testing', value: t.in_testing || 0 },
    { key: 'ready_for_delivery', value: t.ready_for_delivery || 0 },
    { key: 'released', value: t.released || 0 },
    { key: 'done', value: t.done || 0 },
    { key: 'cancelled', value: t.cancelled || 0 },
  ].map(d => ({ name: STATUS_LABELS[d.key] || d.key, value: d.value, color: STATUS_COLORS[d.key] || '#94a3b8' }))
   .filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  const priorityColors = { urgent: '#ef4444', high: '#f97316', medium: '#fbbf24', low: '#94a3b8', none: '#cbd5e1' };
  const priorityData = [
    { name: 'Urgent', value: t.urgent_priority || 0, color: priorityColors.urgent },
    { name: 'High', value: t.high_priority || 0, color: priorityColors.high },
    { name: 'Medium', value: t.medium_priority || 0, color: priorityColors.medium },
    { name: 'Low', value: t.low_priority || 0, color: priorityColors.low },
  ].filter(d => d.value > 0);

  const formatTrendDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-slate-100 px-4 py-3">
        <p className="text-xs font-medium text-slate-500 mb-1">{formatTrendDate(label)}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  const maxLeaderboard = leaderboard.length > 0 ? Math.max(...leaderboard.map(l => l.total)) : 1;
  const maxDaysWorked = leaderboard.length > 0 ? Math.max(...leaderboard.map(l => l.total_days_worked || 0), 1) : 1;
  
  // Sort for "worst performer" by deviation — most positive deviation days = worst
  const deviationRanked = [...leaderboard]
    .filter(l => l.tasks_with_deviation_data > 0)
    .sort((a, b) => b.avg_deviation_days - a.avg_deviation_days);
  // Sort by days worked (most hours on top)
  const hoursRanked = [...leaderboard]
    .filter(l => l.total_days_worked > 0)
    .sort((a, b) => b.total_days_worked - a.total_days_worked);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-sm text-slate-400 mb-1">Welcome back,</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {user?.name || 'Dashboard'}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {isManager && (
            <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5" data-testid="scope-toggle">
              <button
                onClick={() => setScope('all')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${scope === 'all' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="scope-all-btn"
              >
                <BarChart3 className="h-3 w-3 inline mr-1" />Overall
              </button>
              <button
                onClick={() => setScope('my')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${scope === 'my' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="scope-my-btn"
              >
                <Users className="h-3 w-3 inline mr-1" />My Tasks
              </button>
            </div>
          )}
          {['admin', 'pm', 'support'].includes(user?.role) && (
            <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
              <SelectTrigger className="w-36 sm:w-44 rounded-lg bg-white border-slate-200 h-9 text-sm" data-testid="org-filter">
                <Building2 className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Projects</SelectItem>
                {organizations.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Link to="/work-items">
            <button className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-all whitespace-nowrap" data-testid="goto-planboard-btn">
              <ListTodo className="h-4 w-4" /> Open Planboard
            </button>
          </Link>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-cards-row">
        <KPICard value={t.total || 0} label="Total Tasks" icon={ClipboardList} color="#3b82f6" bgColor="#eff6ff" onClick={() => navigate(`/work-items${scope === 'my' ? '?scope=my' : ''}`)} />
        <KPICard value={openCount} label="Open" icon={AlertCircle} color="#f59e0b" bgColor="#fffbeb" sub={`${openRate}% of total`} onClick={() => openCount > 0 && navigate(`/work-items?status=todo,in_progress,backlog,ready_for_testing${scope === 'my' ? '&scope=my' : ''}`)} />
        <KPICard value={(t.done || 0) + (t.released || 0)} label="Completed" icon={CheckCircle2} color="#22c55e" bgColor="#f0fdf4" sub={`${completionRate}% rate`} onClick={() => ((t.done || 0) + (t.released || 0)) > 0 && navigate(`/work-items?status=done,released${scope === 'my' ? '&scope=my' : ''}`)} />
        <KPICard value={t.overdue || 0} label="Overdue" icon={AlertTriangle} color="#ef4444" bgColor="#fef2f2" alert={(t.overdue || 0) > 0} onClick={() => (t.overdue || 0) > 0 && navigate(`/work-items?filter=overdue${scope === 'my' ? '&scope=my' : ''}`)} />
        <div className="rounded-xl bg-slate-900 text-white p-5 relative overflow-hidden" data-testid="kpi-completion-rate">
          <div className="flex items-center justify-between mb-3">
            <Activity className="h-4 w-4 text-white/60" />
            <span className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Health</span>
          </div>
          <div className="text-4xl font-bold tracking-tight">{completionRate}<span className="text-lg text-white/50">%</span></div>
          <p className="text-xs text-white/40 mt-1">Completion Rate</p>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
            <div className="h-full bg-emerald-400 transition-all duration-700" style={{ width: `${completionRate}%` }} />
          </div>
        </div>
      </div>

      {/* My Bucket — Tasks & Subtasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bucket-row">
        {/* Task Bucket */}
        <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="task-bucket">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-blue-500" /> Tasks
            </h3>
            <span className="text-xl font-bold text-slate-900">{t.total || 0}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div className={`rounded-lg bg-amber-50 border border-amber-100 p-3 text-center ${openCount > 0 ? 'cursor-pointer hover:bg-amber-100 transition-colors' : ''}`}
              onClick={() => openCount > 0 && navigate(`/work-items?status=todo,in_progress,backlog,ready_for_testing${scope === 'my' ? '&scope=my' : ''}`)}
              data-testid="bucket-open-link">
              <div className="text-lg font-bold text-amber-600">{openCount}</div>
              <div className="text-[10px] text-amber-500 font-medium">Open</div>
            </div>
            <div className={`rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center ${((t.done || 0) + (t.released || 0)) > 0 ? 'cursor-pointer hover:bg-emerald-100 transition-colors' : ''}`}
              onClick={() => ((t.done || 0) + (t.released || 0)) > 0 && navigate(`/work-items?status=done,released${scope === 'my' ? '&scope=my' : ''}`)}
              data-testid="bucket-completed-link">
              <div className="text-lg font-bold text-emerald-600">{(t.done || 0) + (t.released || 0)}</div>
              <div className="text-[10px] text-emerald-500 font-medium">Completed</div>
            </div>
            <div className={`rounded-lg bg-red-50 border border-red-100 p-3 text-center ${(t.overdue || 0) > 0 ? 'cursor-pointer hover:bg-red-100 transition-colors' : ''}`}
              onClick={() => (t.overdue || 0) > 0 && navigate(`/work-items?filter=overdue${scope === 'my' ? '&scope=my' : ''}`)}
              data-testid="bucket-overdue-link">
              <div className="text-lg font-bold text-red-600">{t.overdue || 0}</div>
              <div className="text-[10px] text-red-500 font-medium">Overdue</div>
            </div>
          </div>
          {/* Status mini-bars */}
          <div className="space-y-1.5">
            {statusData.slice(0, 5).map(s => {
              const pct = t.total > 0 ? Math.round((s.value / t.total) * 100) : 0;
              return (
                <div key={s.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                  <span className="text-[11px] text-slate-500 w-24 truncate">{s.name}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-600 w-8 text-right">{s.value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Subtask Bucket */}
        <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="subtask-bucket">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-violet-500" /> Subtasks
            </h3>
            <span className="text-xl font-bold text-slate-900">{t.total_subtasks || 0}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-center">
              <div className="text-lg font-bold text-amber-600">{(t.total_subtasks || 0) - (t.done_subtasks || 0)}</div>
              <div className="text-[10px] text-amber-500 font-medium">Open</div>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
              <div className="text-lg font-bold text-emerald-600">{t.done_subtasks || 0}</div>
              <div className="text-[10px] text-emerald-500 font-medium">Completed</div>
            </div>
          </div>
          {/* Subtask progress bar */}
          <div className="space-y-1.5">
            {(t.total_subtasks || 0) > 0 ? (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                <span className="text-[11px] text-slate-500 w-24 truncate">Done</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-400" style={{ width: `${t.total_subtasks > 0 ? Math.round((t.done_subtasks || 0) / t.total_subtasks * 100) : 0}%` }} />
                </div>
                <span className="text-[11px] font-semibold text-slate-600 w-8 text-right">{t.done_subtasks || 0}</span>
              </div>
            ) : (
              <p className="text-xs text-slate-300 text-center py-3">No subtasks yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Activities Dashboard */}
      {activityStats && (() => {
        const a = activityStats;
        const aCompleted = (a.done || 0) + (a.released || 0);
        const aOpen = (a.total || 0) - aCompleted - (a.cancelled || 0);
        const aCompletionRate = a.total > 0 ? Math.round(aCompleted / a.total * 100) : 0;
        const aStatusData = [
          { key: 'backlog', value: a.backlog || 0 },
          { key: 'todo', value: a.todo || 0 },
          { key: 'in_progress', value: a.in_progress || 0 },
          { key: 'done', value: a.done || 0 },
          { key: 'released', value: a.released || 0 },
          { key: 'cancelled', value: a.cancelled || 0 },
        ].map(d => ({ name: STATUS_LABELS[d.key] || d.key, value: d.value, color: STATUS_COLORS[d.key] || '#94a3b8' }))
         .filter(d => d.value > 0).sort((a, b) => b.value - a.value);

        return (
          <div className="space-y-4" data-testid="activities-dashboard">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-indigo-500" />
              <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Activities</h2>
              <span className="text-xs text-slate-400">Meetings, Training & Support</span>
            </div>

            {/* Activity KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="activity-kpi-row">
              <KPICard value={a.total || 0} label="Total Activities" icon={Briefcase} color="#6366f1" bgColor="#eef2ff" onClick={() => (a.total || 0) > 0 && navigate('/work-items?view=activities')} />
              <KPICard value={a.meeting || 0} label="Meetings" icon={Presentation} color="#8b5cf6" bgColor="#f5f3ff" onClick={() => (a.meeting || 0) > 0 && navigate('/work-items?view=activities&type=meeting')} />
              <KPICard value={a.training || 0} label="Training" icon={ClipboardList} color="#0ea5e9" bgColor="#f0f9ff" onClick={() => (a.training || 0) > 0 && navigate('/work-items?view=activities&type=training')} />
              <KPICard value={a.support || 0} label="Support" icon={Headphones} color="#14b8a6" bgColor="#f0fdfa" onClick={() => (a.support || 0) > 0 && navigate('/work-items?view=activities&type=support')} />
              <KPICard value={aOpen} label="Open" icon={AlertCircle} color="#f59e0b" bgColor="#fffbeb" onClick={() => aOpen > 0 && navigate('/work-items?view=activities&filter=open')} />
              <KPICard value={a.overdue || 0} label="Overdue" icon={AlertTriangle} color="#ef4444" bgColor="#fef2f2" alert={(a.overdue || 0) > 0} onClick={() => (a.overdue || 0) > 0 && navigate('/work-items?view=activities&filter=overdue')} />
            </div>

            {/* Activity Bucket + Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="activity-bucket">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-indigo-500" /> Activity Summary
                  </h3>
                  <span className="text-xl font-bold text-slate-900">{a.total || 0}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  <div className={`rounded-lg bg-violet-50 border border-violet-100 p-3 text-center ${(a.meeting || 0) > 0 ? 'cursor-pointer hover:bg-violet-100 transition-colors' : ''}`}
                    onClick={() => (a.meeting || 0) > 0 && navigate('/work-items?view=activities&type=meeting')}
                    data-testid="activity-bucket-meeting">
                    <div className="text-lg font-bold text-violet-600">{a.meeting || 0}</div>
                    <div className="text-[10px] text-violet-500 font-medium">Meetings</div>
                  </div>
                  <div className={`rounded-lg bg-sky-50 border border-sky-100 p-3 text-center ${(a.training || 0) > 0 ? 'cursor-pointer hover:bg-sky-100 transition-colors' : ''}`}
                    onClick={() => (a.training || 0) > 0 && navigate('/work-items?view=activities&type=training')}
                    data-testid="activity-bucket-training">
                    <div className="text-lg font-bold text-sky-600">{a.training || 0}</div>
                    <div className="text-[10px] text-sky-500 font-medium">Training</div>
                  </div>
                  <div className={`rounded-lg bg-teal-50 border border-teal-100 p-3 text-center ${(a.support || 0) > 0 ? 'cursor-pointer hover:bg-teal-100 transition-colors' : ''}`}
                    onClick={() => (a.support || 0) > 0 && navigate('/work-items?view=activities&type=support')}
                    data-testid="activity-bucket-support">
                    <div className="text-lg font-bold text-teal-600">{a.support || 0}</div>
                    <div className="text-[10px] text-teal-500 font-medium">Support</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-[11px] text-slate-500">Completion</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-500 transition-all duration-500" style={{ width: `${aCompletionRate}%` }} />
                  </div>
                  <span className="text-[11px] font-semibold text-indigo-600">{aCompletionRate}%</span>
                </div>
              </div>

              <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="activity-status-breakdown">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-800">Activity Status</h3>
                  <BarChart3 className="h-4 w-4 text-slate-300" />
                </div>
                <div className="space-y-3">
                  {aStatusData.map(s => {
                    const pct = a.total > 0 ? Math.round((s.value / a.total) * 100) : 0;
                    return (
                      <div key={s.name} className="group">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                            <span className="text-sm text-slate-600">{s.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800">{s.value}</span>
                            <span className="text-[10px] text-slate-400">{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500 group-hover:opacity-80" style={{ width: `${pct}%`, background: s.color }} />
                        </div>
                      </div>
                    );
                  })}
                  {aStatusData.length === 0 && <p className="text-xs text-slate-300 text-center py-3">No data</p>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* AI Insight Banner */}
      <div className="rounded-xl bg-gradient-to-r from-violet-50 via-white to-violet-50 border border-violet-100 px-6 py-4" data-testid="ai-insights-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-violet-600" />
            </div>
            <div className="flex-1">
              <span className="text-xs font-semibold text-violet-600 uppercase tracking-wider">AI Insight</span>
              {aiInsight ? (
                <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{aiInsight}</p>
              ) : (
                <p className="text-xs text-slate-400 mt-0.5">Click generate for AI-powered project analysis</p>
              )}
            </div>
          </div>
          <button onClick={handleAiInsight} disabled={aiLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors flex-shrink-0 ml-4"
            data-testid="generate-insight-btn">
            {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate
          </button>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Trend Area Chart */}
        <div className="lg:col-span-2 rounded-xl bg-white border border-slate-200 p-6" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Task Activity</h3>
              <p className="text-xs text-slate-400 mt-0.5">Created vs completed over time</p>
            </div>
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="trend-period-toggle">
              {PERIOD_OPTIONS.map(p => (
                <button key={p.value} onClick={() => setTrendPeriod(p.value)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${trendPeriod === p.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`period-${p.value}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  interval={trendPeriod <= 7 ? 0 : trendPeriod <= 30 ? 4 : 14} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="created" name="Created" stroke="#3b82f6" strokeWidth={2} fill="url(#gradCreated)" dot={false} />
                <Area type="monotone" dataKey="completed" name="Completed" stroke="#22c55e" strokeWidth={2} fill="url(#gradCompleted)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-3">
            <div className="flex items-center gap-2"><div className="w-3 h-0.5 rounded-full bg-blue-500" /><span className="text-[11px] text-slate-500">Created</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-0.5 rounded-full bg-emerald-500" /><span className="text-[11px] text-slate-500">Completed</span></div>
          </div>
        </div>

        {/* Priority Donut */}
        <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="priority-chart">
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Priority Split</h3>
          <p className="text-xs text-slate-400 mb-4">Distribution across tasks</p>
          <div className="h-[180px]">
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={priorityData} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                    paddingAngle={3} dataKey="value" nameKey="name" stroke="none">
                    {priorityData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(val, name) => [`${val} tasks`, name]} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="flex items-center justify-center h-full text-slate-300 text-xs">No data</div>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {priorityData.map(p => (
              <div key={p.name} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                <span className="text-[11px] text-slate-500">{p.name} <strong className="text-slate-700">{p.value}</strong></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sprint Status by Users */}
      {sprintUserStats?.sprints_list?.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200" data-testid="sprint-user-stats-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-5 border-b border-slate-100 gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={selectedSprintId || sprintUserStats.sprint?.id || ''}
                onChange={(e) => setSelectedSprintId(e.target.value)}
                className="h-8 px-2 text-sm font-semibold border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-[#002FA7] cursor-pointer max-w-[200px] sm:max-w-[260px]"
                data-testid="sprint-selector"
              >
                {sprintUserStats.sprints_list.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.task_count}) {s.status === 'active' ? '●' : ''}
                  </option>
                ))}
              </select>
              {sprintUserStats.sprint && (
                <p className="text-xs text-slate-400">
                  {sprintUserStats.sprint.start_date ? new Date(sprintUserStats.sprint.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'} → {sprintUserStats.sprint.end_date ? new Date(sprintUserStats.sprint.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                  <span className="ml-2 font-medium">{sprintUserStats.sprint.done}/{sprintUserStats.sprint.total} done</span>
                </p>
              )}
            </div>
            {sprintUserStats.sprint && (
              <div className="flex items-center gap-3">
                <div className="w-24 sm:w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#002FA7] transition-all" style={{ width: `${sprintUserStats.sprint.progress}%` }} />
                </div>
                <span className="text-sm font-bold text-[#002FA7]">{sprintUserStats.sprint.progress}%</span>
              </div>
            )}
          </div>

          {sprintUserStats.users?.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-5 py-2.5">User</th>
                  <th className="text-center px-3 py-2.5">Total</th>
                  <th className="text-center px-3 py-2.5">Done</th>
                  <th className="text-center px-3 py-2.5">Pending</th>
                  <th className="text-center px-3 py-2.5">Overdue</th>
                  <th className="text-right px-3 py-2.5">Est. Hours</th>
                  <th className="text-right px-5 py-2.5">Actual Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sprintUserStats.users.map((u) => {
                  const completionPct = u.total > 0 ? Math.round((u.done / u.total) * 100) : 0;
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`sprint-user-${u.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-[#002FA7]">{(u.name || '?')[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-slate-700">{u.name}</span>
                            <div className="w-16 h-1 bg-slate-100 rounded-full overflow-hidden mt-1">
                              <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${completionPct}%` }} />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-3 py-3 text-sm text-slate-600">{u.total}</td>
                      <td className="text-center px-3 py-3"><span className="text-sm font-semibold text-emerald-600">{u.done}</span></td>
                      <td className="text-center px-3 py-3"><span className={`text-sm font-semibold ${u.pending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{u.pending}</span></td>
                      <td className="text-center px-3 py-3">{u.overdue > 0 ? <span className="text-sm font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">{u.overdue}</span> : <span className="text-slate-300">0</span>}</td>
                      <td className="text-right px-3 py-3 text-sm text-slate-500">{u.estimated_hours}h</td>
                      <td className="text-right px-5 py-3 text-sm font-semibold text-slate-800">{u.actual_hours}h</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Layout */}
          <div className="md:hidden divide-y divide-slate-100">
            {sprintUserStats.users.map((u) => {
              const completionPct = u.total > 0 ? Math.round((u.done / u.total) * 100) : 0;
              return (
                <div key={u.id} className="p-3" data-testid={`sprint-user-mobile-${u.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-[#002FA7]">{(u.name || '?')[0]?.toUpperCase()}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-800 truncate">{u.name}</span>
                    </div>
                    <span className="text-xs font-bold text-[#002FA7] flex-shrink-0">{completionPct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2.5">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${completionPct}%` }} />
                  </div>
                  <div className="grid grid-cols-5 gap-1 text-center">
                    <div className="bg-slate-50 rounded py-1.5">
                      <div className="text-xs font-bold text-slate-700">{u.total}</div>
                      <div className="text-[8px] text-slate-400 uppercase font-medium">Total</div>
                    </div>
                    <div className="bg-emerald-50 rounded py-1.5">
                      <div className="text-xs font-bold text-emerald-600">{u.done}</div>
                      <div className="text-[8px] text-emerald-500 uppercase font-medium">Done</div>
                    </div>
                    <div className="bg-amber-50 rounded py-1.5">
                      <div className="text-xs font-bold text-amber-600">{u.pending}</div>
                      <div className="text-[8px] text-amber-500 uppercase font-medium">Pend</div>
                    </div>
                    <div className={`rounded py-1.5 ${u.overdue > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                      <div className={`text-xs font-bold ${u.overdue > 0 ? 'text-red-500' : 'text-slate-400'}`}>{u.overdue}</div>
                      <div className="text-[8px] text-slate-400 uppercase font-medium">Over</div>
                    </div>
                    <div className="bg-blue-50 rounded py-1.5">
                      <div className="text-xs font-bold text-blue-700">{u.actual_hours}<span className="text-[8px] font-normal text-blue-400">/{u.estimated_hours}h</span></div>
                      <div className="text-[8px] text-blue-400 uppercase font-medium">Hrs</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
            </>
          ) : (
            <div className="py-8 text-center text-sm text-slate-400">
              No tasks assigned in this sprint yet.
            </div>
          )}
        </div>
      )}

      {/* Leaderboard + Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Team Leaderboard */}
        <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="leaderboard-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Team Leaderboard</h3>
              <p className="text-xs text-slate-400 mt-0.5">Performance metrics</p>
            </div>
            <Users className="h-4 w-4 text-slate-300" />
          </div>
          {/* Tab switcher */}
          <div className="flex gap-1 mb-4 bg-slate-100 rounded-md p-0.5" data-testid="lb-tabs">
            {[
              { key: 'tasks', label: 'Tasks' },
              { key: 'hours', label: 'Days Worked' },
              { key: 'deviation', label: 'Deviations' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setLbTab(tab.key)}
                className={`flex-1 text-[11px] font-medium py-1.5 rounded transition-all ${lbTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`lb-tab-${tab.key}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-2.5">
            {lbTab === 'tasks' && (
              <>
                {leaderboard.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No assignee data</p>}
                {leaderboard.slice(0, 8).map((lb, i) => (
                  <div key={lb.id} className="flex items-center gap-3" data-testid={`lb-row-${i}`}>
                    <span className="text-xs font-bold text-slate-300 w-4 text-right">{i + 1}</span>
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-slate-500">{lb.name?.substring(0,2)?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700 truncate">{lb.name}</span>
                        <span className="text-xs font-semibold text-slate-800">{lb.total}</span>
                      </div>
                      <div className="flex gap-0.5 h-1.5 rounded-full overflow-hidden bg-slate-100">
                        <div className="h-full bg-emerald-400 rounded-l-full transition-all" style={{ width: `${(lb.done / maxLeaderboard) * 100}%` }} title={`Done: ${lb.done}`} />
                        <div className="h-full bg-blue-400 transition-all" style={{ width: `${(lb.in_progress / maxLeaderboard) * 100}%` }} title={`In Progress: ${lb.in_progress}`} />
                        <div className="h-full bg-slate-200 rounded-r-full transition-all" style={{ width: `${((lb.total - lb.done - lb.in_progress) / maxLeaderboard) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
                {leaderboard.length > 0 && (
                  <div className="flex items-center gap-4 mt-3 pt-2.5 border-t border-slate-100">
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-400" /><span className="text-[10px] text-slate-400">Done</span></div>
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-400" /><span className="text-[10px] text-slate-400">In Progress</span></div>
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-slate-200" /><span className="text-[10px] text-slate-400">Other</span></div>
                  </div>
                )}
              </>
            )}

            {lbTab === 'hours' && (
              <>
                {hoursRanked.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data — set Start & Release dates on tasks</p>}
                {hoursRanked.slice(0, 8).map((lb, i) => (
                  <div key={lb.id} className="flex items-center gap-3" data-testid={`lb-hours-row-${i}`}>
                    <span className="text-xs font-bold text-slate-300 w-4 text-right">{i + 1}</span>
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-3.5 w-3.5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700 truncate">{lb.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-blue-600">{lb.total_days_worked}d</span>
                          <span className="text-[10px] text-slate-400">({lb.tasks_with_hours} tasks)</span>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden bg-slate-100">
                        <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${(lb.total_days_worked / maxDaysWorked) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
                {hoursRanked.length > 0 && (
                  <p className="text-[10px] text-slate-400 mt-3 pt-2.5 border-t border-slate-100">Days = Release Date − Start Date for completed tasks</p>
                )}
              </>
            )}

            {lbTab === 'deviation' && (
              <>
                {deviationRanked.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data — set Release Date & Target Release Date on tasks</p>}
                {deviationRanked.slice(0, 8).map((lb, i) => {
                  const isLate = lb.avg_deviation_days > 0;
                  const isEarly = lb.avg_deviation_days < 0;
                  return (
                    <div key={lb.id} className={`flex items-center gap-3 p-2 rounded-lg ${i === 0 && isLate ? 'bg-red-50/60 border border-red-100' : ''}`} data-testid={`lb-dev-row-${i}`}>
                      <span className="text-xs font-bold text-slate-300 w-4 text-right">{i + 1}</span>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isLate ? 'bg-red-50' : isEarly ? 'bg-green-50' : 'bg-slate-50'}`}>
                        {isLate ? <TrendingDown className="h-3.5 w-3.5 text-red-400" /> : <TrendingUp className="h-3.5 w-3.5 text-green-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700 truncate">{lb.name}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold ${isLate ? 'text-red-500' : isEarly ? 'text-green-600' : 'text-slate-500'}`}>
                              {isLate ? '+' : ''}{lb.avg_deviation_days}d avg
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-slate-400">{lb.tasks_deviated} late / {lb.tasks_with_deviation_data} total</span>
                          <span className="text-[10px] text-slate-400">Total: {lb.total_deviation_days > 0 ? '+' : ''}{lb.total_deviation_days}d</span>
                        </div>
                      </div>
                      {i === 0 && isLate && (
                        <span className="text-[9px] font-bold text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 flex-shrink-0">
                          WORST
                        </span>
                      )}
                    </div>
                  );
                })}
                {deviationRanked.length > 0 && (
                  <p className="text-[10px] text-slate-400 mt-3 pt-2.5 border-t border-slate-100">Deviation = Release Date − Target Release Date (positive = late delivery)</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Status Breakdown - Horizontal Bars */}
        <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="status-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Status Breakdown</h3>
              <p className="text-xs text-slate-400 mt-0.5">Current distribution</p>
            </div>
            <BarChart3 className="h-4 w-4 text-slate-300" />
          </div>
          <div className="space-y-3">
            {statusData.map(s => {
              const pct = t.total > 0 ? Math.round((s.value / t.total) * 100) : 0;
              return (
                <div key={s.name} className="group" data-testid={`status-bar-${s.name}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                      <span className="text-sm text-slate-600">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{s.value}</span>
                      <span className="text-[10px] text-slate-400">{pct}%</span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500 group-hover:opacity-80" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Overdue Tasks */}
      {(t.overdue || 0) > 0 && (
        <div className="rounded-xl border border-red-200 bg-white overflow-hidden" data-testid="overdue-tasks-section">
          <div className="px-6 py-4 bg-red-50 border-b border-red-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {t.overdue} Overdue Tasks
            </h3>
            <Link to={`/work-items?filter=overdue${scope === 'my' ? '&scope=my' : ''}`} className="text-xs font-medium text-red-600 hover:underline flex items-center gap-1" data-testid="overdue-view-all-link">View all <ArrowRight className="h-3 w-3" /></Link>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/work-items" icon={ListTodo} color="#3b82f6" title="Planboard" desc="Grid-based task management" />
        <QuickLink to="/kanban" icon={FolderKanban} color="#a855f7" title="Kanban Board" desc="Drag & drop workflows" />
        <QuickLink to="/projects" icon={GanttChart} color="#22c55e" title="Projects" desc="Sprints & timelines" />
      </div>
    </div>
  );
};

const KPICard = ({ value, label, icon: Icon, color, bgColor, sub, alert, onClick }) => (
  <div className={`rounded-xl bg-white border p-5 transition-all hover:shadow-sm ${alert ? 'border-red-200' : 'border-slate-200'} ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: bgColor }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      {alert && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
    </div>
    <div className="text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    <p className="text-xs text-slate-400 mt-1">{label}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const QuickLink = ({ to, icon: Icon, color, title, desc }) => (
  <Link to={to} className="rounded-xl p-5 bg-white border border-slate-200 hover:border-slate-300 transition-all hover:-translate-y-[1px] hover:shadow-sm group" data-testid={`quick-link-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
    </div>
    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
  </Link>
);

export default Dashboard;
